import { getList as getTenants } from '@/components/TenantBar/service';
import { getClusters, getClustersNoLogin, getTenantClusters } from '@/pages/clusters/service';
import {
  TENANT_CLUSTER_MAP_STORAGE_KEY,
  getTenantClusterByTenant,
  isValidClusterName,
  setTenantClusterForTenant,
} from '@/utils/tenantClusterStorage';
import { _getStorage, _setStorage } from '@/utils/useLocalStorage';

/**
 * 封装本地存储的键名，避免硬编码
 */
export const STORAGE_KEYS = {
  MODE: 'mode',
  TENANT: 'tenant',
  CLUSTER: 'cluster',
  TENANT_CLUSTERS: 'tenantClusters',
  TENANT_CLUSTER_MAP: TENANT_CLUSTER_MAP_STORAGE_KEY,
};
const defaultReturn = { value: undefined, data: [] };

/**
 * 获取当前用户的数据中心列表
 * @async
 */
export const getDcListAndVal = async ({
  isLogin = true,
  initialValue,
  filterOnline = true,
}: {
  isLogin?: boolean;
  initialValue?: string;
  filterOnline?: boolean;
} = {}) => {
  try {
    const data = isLogin ? await getClusters() : await getClustersNoLogin();
    if (Array.isArray(data)) {
      const onlineData = filterOnline ? data.filter((c) => c.live) : data;

      // 默认选中逻辑：优先使用 URL 参数，其次是本地存储，最后是第一个在线集群
      const defaultClusterName = initialValue || _getStorage(STORAGE_KEYS.CLUSTER);
      const selectedCluster = onlineData.find((c) => c.name === defaultClusterName);
      const finalSelectedClusterName = selectedCluster?.name || onlineData?.[0]?.name;

      filterOnline && localStorage.setItem(STORAGE_KEYS.CLUSTER, JSON.stringify(finalSelectedClusterName));
      return { data: onlineData, value: finalSelectedClusterName };
    }
    return defaultReturn;
  } catch (e) {
    console.error(e);
    return defaultReturn;
  }
};

/**
 * 获取当前用户的租户列表
 * @async
 */
export const getTenantListAndVal = async ({ initialValue }: { initialValue?: string } = {}) => {
  try {
    const data = await getTenants('');
    if (Array.isArray(data)) {
      // 默认选中逻辑：优先使用 URL 参数，其次是本地存储，最后是列表第一个租户
      const defaultTenantName = initialValue || _getStorage(STORAGE_KEYS.TENANT);
      const selectedTenant = data.find((item) => item.name === defaultTenantName) || data[0];
      const selectedTenantName = selectedTenant?.name;

      localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(selectedTenantName));
      return { data, value: selectedTenantName };
    }
    return defaultReturn;
  } catch (e) {
    console.error(e);
    return defaultReturn;
  }
};

/**
 * 获取当前租户的集群列表
 * @async
 */
let lastTenant = '';
export const getDcAndValByTenant = async ({
  tenant,
  tenantCluster = undefined,
  filterOnline = true,
  useCache = true,
}: {
  tenant: string | undefined;
  tenantCluster?: string;
  filterOnline?: boolean;
  useCache?: boolean;
}) => {
  if (!tenant || tenant === 'undefined' || tenant === ':tenant') return defaultReturn;
  try {
    let items: any[] = [];
    const storedTenantClusters = _getStorage(STORAGE_KEYS.TENANT_CLUSTERS, []);
    if (storedTenantClusters?.length && lastTenant === tenant && useCache) {
      items = storedTenantClusters;
    } else {
      lastTenant = tenant;
      ({ items } = (await getTenantClusters({}, tenant)) as { items: any[] });
    }
    if (Array.isArray(items)) {
      // 过滤掉不在线的集群
      const filtersArr = filterOnline ? items.filter((i) => i.live) : items;
      // 默认选中当前租户缓存的数据中心；URL 参数优先，其次读取 tenantClusterMap。
      // 忽略字面量 "undefined" / 占位符，避免错误选中。
      const rawStored = tenantCluster || getTenantClusterByTenant(tenant);
      const storedTenantCluster = isValidClusterName(rawStored) ? rawStored : undefined;
      const defaultCluster = filtersArr?.find((i) => i?.name === storedTenantCluster)?.name;
      const cluster1 = filtersArr?.[0]?.name;
      const selectedCluster = defaultCluster || cluster1;
      filterOnline && _setStorage(STORAGE_KEYS.TENANT_CLUSTERS, filtersArr);
      setTenantClusterForTenant(tenant, selectedCluster);
      return { value: selectedCluster, data: filtersArr };
    }
    return defaultReturn;
  } catch (e) {
    console.error(e);
    return defaultReturn;
  }
};
